import { Theme } from "@mui/material";
import { round } from "lodash";

import { ABBREVIATED_OPTION } from "@/hooks/useConfig";
import { FormatCurrencyFunction } from "@/hooks/useConfig/types";

interface BubbleChartData {
  roas: { total: number };
  contribution: { total: number };
  spend: { total: number };
  nodeName?: string;
  insufficientSpend?: boolean;
}

interface TooltipParams {
  value?: number[];
  color: string;
  name: string;
}

// Constants
const LIGHT_GRAY = "#6E6E6E";

// Helper functions for rendering individual metric rows
const renderMetricRow = (label: string, value: string | number) => `
  <div style="display: flex; flex-direction: column;">
    <span style="font-weight: 600;">${label}</span>
    <span>${value}</span>
  </div>
`;

// Renders a single channel group's metrics
const renderChannelMetrics = (
  roas: number,
  contribution: number,
  spend: string,
  theme: Theme,
  insufficientSpend?: boolean,
  nodeName?: string
) => {
  const metrics = [
    renderMetricRow("ROAS %", `${roas}%`),
    renderMetricRow("Revenue Contribution %", `${contribution}%`),
    renderMetricRow("Spend", spend),
  ].join("");

  return `
    <div style="display: flex; gap: 24px;">
      <div style="display: flex; flex-direction: column; gap: 8px;">
        ${nodeName ? `<span style="font-weight: 600; color: ${theme.palette.mode === "dark" ? "white" : LIGHT_GRAY};">${nodeName}</span>` : ""}
        ${insufficientSpend ? "Insufficient data" : metrics}
      </div>
    </div>
  `;
};

// Renders comparison view between multiple channel groups
const renderComparisonView = (
  channelGroups: BubbleChartData[],
  targetNodeName: string,
  spendFormatter: (value: number) => string,
  contributionFormatter: (value: number) => number,
  theme: Theme
) => {
  const channelMetrics = channelGroups
    .map((channelGroup) =>
      renderChannelMetrics(
        channelGroup.roas.total,
        contributionFormatter(channelGroup.contribution.total),
        spendFormatter(channelGroup.spend.total),
        theme,
        channelGroup.insufficientSpend,
        channelGroup.nodeName || targetNodeName
      )
    )
    .join("");

  return `
    <div style='display: grid; grid-template-columns: repeat(2, 1fr); grid-row-gap: 24px; grid-column-gap: 48px;'>
      ${channelMetrics}
    </div>
  `;
};

// Main tooltip formatter function
export const formatTooltip = (
  params: TooltipParams,
  formatCurrency: FormatCurrencyFunction,
  theme: Theme,
  targetNodeName?: string,
  channelGroups?: BubbleChartData[]
) => {
  const values = params.value || [];
  const [roas = 0, contribution = 0, spend = 0] = values;

  const spendFormatter = (value: number) => formatCurrency(value, ABBREVIATED_OPTION);
  const contributionFormatter = (value: number) => round(value, 2);

  const header = `
    <div style="margin-bottom: 16px; border-bottom: 1px solid ${theme.palette.divider}; padding-bottom: 16px;">
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${params.color};"></span>
        <h3 style="font-size: 20px; font-weight: 600; margin: 0;">${params.name}</h3>
      </div>
    </div>
  `;

  const content =
    channelGroups && channelGroups.length > 1
      ? renderComparisonView(
          channelGroups,
          targetNodeName || "",
          spendFormatter,
          contributionFormatter,
          theme
        )
      : renderChannelMetrics(
          roas,
          contributionFormatter(contribution),
          spendFormatter(spend),
          theme
        );

  return `
    <div style="padding: 24px; min-width: 180px; color: ${theme.palette.text.primary};">
      ${header}
      ${content}
    </div>
  `;
};
